import React, { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../../Navbar/Navbar";
import baseURL from "../../config";
import "./CrewingAgentForm.css";
import { countries } from 'countries-list';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

function CrewingAgentForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const [phoneCountryIso, setPhoneCountryIso] = useState("in");

  const countryList = Object.entries(countries).map(([code, country]) => ({
    code,
    name: country.name,
    dialCode: country.phone, // Add this
    iso2: code.toLowerCase(), // For PhoneInput
  }));

  const formatDate = (dateStr) => {
    return dateStr ? new Date(dateStr).toISOString().split('T')[0] : "";
  }

  const [formData, setFormData] = useState(() => {
    const agent = location.state?.agent || {};
    const seniorManager = agent.seniorManager || [];

    return {
      ...(agent._id ? { _id: agent._id } : {}),
      agentName: agent.agentName || "",
      shortName: agent.shortName || "",
      address: agent.address || "",
      contactPersonTitle: agent.contactPerson?.title || "",
      contactPersonName: agent.contactPerson?.name || "",
      country: agent.country || "",
      contactNumber: agent.contactNumber || "",
      email: agent.email || "",
      cirtificatteOfIncorporation: agent.cirtificatteOfIncorporation || null,
      RPSLicense: agent.RPSLicense || null,
      ISOCertificate: agent.ISOCertificate || null,
      RPSLicenseIssuedDate: formatDate(agent.RPSLicenseIssuedDate),
      RPSLicenseExpiryDate: formatDate(agent.RPSLicenseExpiryDate),
      ISOCertificateIssuedDate: formatDate(agent.ISOCertificateIssuedDate),
      ISOCertificateExpiryDate: formatDate(agent.ISOCertificateExpiryDate),
      Audit: formatDate(agent.Audit),
      AuditStatus: agent.AuditStatus || "",
      // seniorManager: [{ name: "", number: "", email: "" }], 
      seniorManager: seniorManager.length > 0 ? seniorManager : [{ name: "", number: "", email: "" }],
    };
  });

  const [fileMetadata, setFileMetadata] = useState({
    cirtificatteOfIncorporation: null,
    RPSLicense: null,
    ISOCertificate: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "country") {
      const selectedCountry = countryList.find((c) => c.name === value);
      const dialCode = selectedCountry?.dialCode || "";
      const iso2 = selectedCountry?.iso2 || "in";

      setPhoneCountryIso(iso2); // update PhoneInput

      setFormData((prev) => ({
        ...prev,
        country: value,
        contactNumber: `+${dialCode}`, // auto update number field
      }));
    }
    else if (name === "contactNumber") {
      setFormData((prev) => {
        const existingDialCode = (prev.contactNumber.match(/^\+\d+/) || [""])[0];
        const restNumber = value.replace(existingDialCode, "").replace(/\D/g, "");
        return {
          ...prev,
          contactNumber: `${existingDialCode} ${restNumber}`,
        };
      });

    } else if (name.startsWith("seniorManager")) {
      const parts = name.split(".");
      const index = parseInt(parts[1], 10);
      const field = parts[2];

      setFormData((prev) => {
        const updatedSeniorManagers = [...prev.seniorManager];
        updatedSeniorManagers[index][field] =
          field === "number" && !value.startsWith("+")
            ? updatedSeniorManagers[index][field] + value
            : value;

        return {
          ...prev,
          seniorManager: updatedSeniorManagers,
        };
      });

    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const formatFileSize = (sizeInBytes) => {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;

    if (files.length === 0) return;
    const file = files[0];
    setFormData((prev) => ({
      ...prev,
      [name]: file,
    }));

    setFileMetadata((prev) => ({
      ...prev,
      [name]: {
        name: file.name,
        size: formatFileSize(file.size),
        type: file.type,
        lastModified: new Date(file.lastModified).toLocaleDateString(),
      },
    }));
  };

  const removeFile = (name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: null,
    }));

    setFileMetadata((prev) => ({
      ...prev,
      [name]: null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.cirtificatteOfIncorporation) {
      toast.error("Please upload the Certificate of Incorporation.");
      return;
    }

    if (!formData.RPSLicense) {
      toast.error("Please upload the RPS License.");
      return;
    }
    if (!formData.ISOCertificate) {
      toast.error("Please upload the ISO Certificate.");
      return;
    }

    const form = new FormData();

    // Append all form data
    form.append("agentName", formData.agentName);
    form.append("shortName", formData.shortName);
    form.append("address", formData.address);
    form.append("contactPersonTitle", formData.contactPersonTitle);
    form.append("contactPersonName", formData.contactPersonName);
    form.append("country", formData.country);
    form.append("contactNumber", formData.contactNumber);
    form.append("email", formData.email);
    form.append("RPSLicenseIssuedDate", formData.RPSLicenseIssuedDate);
    form.append("RPSLicenseExpiryDate", formData.RPSLicenseExpiryDate);
    form.append("ISOCertificateIssuedDate", formData.ISOCertificateIssuedDate);
    form.append("ISOCertificateExpiryDate", formData.ISOCertificateExpiryDate);
    form.append("Audit", formData.Audit);
    form.append("AuditStatus", formData.AuditStatus);

    // ✅ Fix: Append seniorManager as an array of objects
    formData.seniorManager.forEach((manager, index) => {
      form.append(`seniorManager[${index}][name]`, manager.name);
      form.append(`seniorManager[${index}][number]`, manager.number);
      form.append(`seniorManager[${index}][email]`, manager.email);
    });

    // Add files if they exist
    if (formData.cirtificatteOfIncorporation)
      form.append("cirtificatteOfIncorporation", formData.cirtificatteOfIncorporation);

    if (formData.RPSLicense)
      form.append("RPSLicense", formData.RPSLicense);

    if (formData.ISOCertificate)
      form.append("ISOCertificate", formData.ISOCertificate);

    try {
      let response;
      const token = localStorage.getItem("token");

      if (formData._id) {
        response = await axios.put(`${baseURL}/api/agentupdate/${formData._id}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        response = await axios.post(`${baseURL}/api/crewingAgentDetails`, form, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 201) {
          toast.success("Crewing Agent details submitted successfully!");
          setTimeout(() => navigate("/login"), 2000);
        }
      }

      if (response.status === 200 && formData._id) {
        toast.success("Crewing Agent details updated successfully!");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.response?.data?.message || "An error occurred while submitting the form.");
    }
  };

  // File upload icon component
  const FileUploadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );

  // File type icon component
  const FileTypeIcon = ({ type }) => {
    // Default document icon
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    );
  };

  // Delete icon component
  const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );

  // File upload component
  const FileUpload = ({ name, label, value, onChange, metadata }) => (
    <div className="input-group file-group">
      <label htmlFor={name}>{label}</label>
      <div className="file-input-wrapper">
        <div className="file-input-container">
          <input
            type="file"
            id={name}
            name={name}
            onChange={onChange}
            className="file-input"
          />
          <div className="file-input-button">
            <FileUploadIcon />
            <span>{metadata ? "Change file" : "Choose file"}</span>
          </div>
        </div>

        {metadata && (
          <div className="file-input-preview">
            <FileTypeIcon type={metadata.type} />
            <div className="file-input-info">
              <div className="file-input-name" title={metadata.name}>
                {metadata.name}
              </div>
              <div className="file-input-meta">
                {metadata.size} • Last modified: {metadata.lastModified}
              </div>
            </div>
            <div className="file-input-actions">
              <button
                type="button"
                className="file-input-remove"
                onClick={() => removeFile(name)}
                title="Remove file"
              >
                <DeleteIcon />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="crewing-agent-container">
      <Navbar />
      <div className="form-container">
        <div className="form-header">
          <h1>Create New Crewing Agent</h1>
          {/* <Link to="/CrewingAgentTable" className="back-button">
            <span>← Back to List</span>
          </Link> */}
        </div>

        <form onSubmit={handleSubmit} className="agent-form">
          <div className="form-card">
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="country">
                  Country <span className="required">*</span>
                </label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Country</option>
                  {countryList.map((country) => (
                    <option key={country.code} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="input-group">
                <label htmlFor="agentName">Agent Name<span className="required">*</span></label>
                <input
                  type="text"
                  id="agentName"
                  name="agentName"
                  placeholder="Enter full agent name"
                  value={formData.agentName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="input-group">
                <label htmlFor="shortName">Short Name</label>
                <input
                  type="text"
                  id="shortName"
                  name="shortName"
                  placeholder="Abbreviation or common name"
                  value={formData.shortName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="address">Address<span className="required">*</span></label>
              <textarea
                id="address"
                name="address"
                placeholder="Enter complete business address"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>
            <div className="crewing-agent-form">
              <div className="form-row">
                {/* Email Address */}
                <div className="input-group">
                  <label htmlFor="email">
                    Email Address<span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="company@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Contact Number */}
                <div className="input-group">
                  <label htmlFor="contactNumber">
                    Contact Number<span className="required">*</span>
                  </label>
                  <PhoneInput
                    country={phoneCountryIso || 'in'}
                    value={formData.contactNumber}
                    onChange={(phone, countryData) => {
                      setFormData((prev) => ({
                        ...prev,
                        contactNumber: phone,
                      }));
                      setPhoneCountryIso(countryData.countryCode); // update selected country
                    }}
                    inputProps={{
                      name: 'contactNumber',
                      required: true,
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "38px",
                      borderRadius: "4px",
                    }}
                    containerStyle={{
                      width: "100%",
                    }}
                    dropdownStyle={{
                      width: "300px",
                      zIndex: 9999,
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="form-divider">
              <span>Authorised Person</span>
            </div>

            <div className="form-row">
              <div className="input-group">
                <label htmlFor="contactPersonTitle">Title</label>
                <input
                  type="text"
                  id="contactPersonTitle"
                  name="contactPersonTitle"
                  placeholder="e.g. Manager, Director"
                  value={formData.contactPersonTitle}
                  onChange={handleChange}
                />
              </div>

              <div className="input-group">
                <label htmlFor="contactPersonName">Full Name<span className="required">*</span></label>
                <input
                  type="text"
                  id="contactPersonName"
                  name="contactPersonName"
                  placeholder="Enter contact person's name"
                  value={formData.contactPersonName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-divider">
              <span>Senior Manager Details</span>
            </div>

            {formData.seniorManager.map((manager, index) => (
              <div className="form-row" key={index}>
                <div className="input-group">
                  <label htmlFor={`seniorManager.${index}.name`}>Name</label>
                  <input
                    type="text"
                    id={`seniorManager.${index}.name`}
                    name={`seniorManager.${index}.name`}
                    placeholder="Enter senior manager's name"
                    value={manager.name}
                    onChange={handleChange}
                  />
                </div>

                <div className="input-group">
                  <label htmlFor={`seniorManager.${index}.number`}>Phone Number</label>
                  <PhoneInput
                    country={phoneCountryIso || 'in'}
                    value={manager.number}
                    onChange={(value, countryData) => {
                      setFormData((prev) => {
                        const updated = [...prev.seniorManager];
                        updated[index].number = value;
                        return {
                          ...prev,
                          seniorManager: updated,
                        };
                      });
                    }}
                    inputProps={{
                      name: `seniorManager.${index}.number`,
                      required: false, // make true if needed
                    }}
                    inputStyle={{
                      width: '100%',
                      height: '38px',
                      borderRadius: '4px',
                    }}
                    containerStyle={{
                      width: '100%',
                    }}
                    dropdownStyle={{
                      width: '300px',
                      zIndex: 9999,
                    }}
                  />
                </div>


                <div className="input-group">
                  <label htmlFor={`seniorManager.${index}.email`}>Email</label>
                  <input
                    type="email"
                    id={`seniorManager.${index}.email`}
                    name={`seniorManager.${index}.email`}
                    placeholder="manager@example.com"
                    value={manager.email}
                    onChange={handleChange}
                  />
                </div>
              </div>
            ))}


            <div className="form-divider">
              <span>Documents & Certifications</span>
            </div>

            <FileUpload
              name="cirtificatteOfIncorporation"
              label="Certificate of Incorporation *"
              value={formData.cirtificatteOfIncorporation}
              onChange={handleFileChange}
              metadata={fileMetadata.cirtificatteOfIncorporation}
            />

            <FileUpload
              name="RPSLicense"
              label="RPS License *"
              value={formData.RPSLicense}
              onChange={handleFileChange}
              metadata={fileMetadata.RPSLicense}
            />

            <div className="form-row">
              <div className="input-group">
                <label htmlFor="RPSLicenseIssuedDate">RPS License Issued Date</label>
                <input
                  type="date"
                  id="RPSLicenseIssuedDate"
                  name="RPSLicenseIssuedDate"
                  value={formData.RPSLicenseIssuedDate || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="input-group">
                <label htmlFor="RPSLicenseExpiryDate">RPS License Expiry Date</label>
                <input
                  type="date"
                  id="RPSLicenseExpiryDate"
                  name="RPSLicenseExpiryDate"
                  value={formData.RPSLicenseExpiryDate || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <FileUpload
              name="ISOCertificate"
              label="ISO Certificate *"
              value={formData.ISOCertificate}
              onChange={handleFileChange}
              metadata={fileMetadata.ISOCertificate}
            />

            <div className="form-row">
              <div className="input-group">
                <label htmlFor="ISOCertificateIssuedDate">ISO Certificate Issued Date </label>
                <input
                  type="date"
                  id="ISOCertificateIssuedDate"
                  name="ISOCertificateIssuedDate"
                  value={formData.ISOCertificateIssuedDate || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="input-group">
                <label htmlFor="ISOCertificateExpiryDate">ISO Certificate Expiry Date</label>
                <input
                  type="date"
                  id="ISOCertificateExpiryDate"
                  name="ISOCertificateExpiryDate"
                  value={formData.ISOCertificateExpiryDate || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-divider">
              <span>Audit Information</span>
            </div>

            <div className="form-row">
              <div className="input-group">
                <label htmlFor="Audit">Audit Date</label>
                <input
                  type="date"
                  id="Audit"
                  name="Audit"
                  value={formData.Audit || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="input-group">
                <label htmlFor="AuditStatus">Audit Status</label>
                <input
                  type="text"
                  id="AuditStatus"
                  name="AuditStatus"
                  placeholder="Enter current audit status"
                  value={formData.AuditStatus}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-button">
                {formData._id ? "Update Agent Details" : "Submit Agent Details"}
              </button>
            </div>
          </div>
        </form>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default CrewingAgentForm;